import { Injector } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  APP_ZONES,
  BaseWidgetProperties,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { take, tap } from 'rxjs';
import { CancelCardAction } from '../../../actions/card-management.action';
import { GetCards } from '../../../actions/card.actions';
import { CardState } from '../../../state/card.state';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Handles the cancellation of a card by dispatching a CancelCardAction
 * with the current card ID. If the card ID is missing, an error alert
 * is shown. Upon successful cancellation, redirects to the 'card-cancelled'
 * page using the provided router and widget properties.
 *
 * @param injector - The Angular Injector used to retrieve necessary services.
 */
export function onCancelCard(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const cardId = store.selectSnapshot(CardState.getCurrentCardId);
  if (!cardId) {
    alertHandlerService.showAlertFn('error', 'Card information is missing.');
    return;
  }
  store
    .dispatch(new CancelCardAction(cardId))
    .pipe(
      take(1),
      tap({
        next: () => {
          const updatedWidgetProperties = {
            ...widgetProperties,
            textContent: '',
            zoneToNavigate: APP_ZONES.CARD_MANAGEMENT_ZONE,
            urlToNavigate: 'card-cancelled',
          };
          handleRedirection(updatedWidgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true, false);
        },
        error: () => {
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true, false);
        },
      }),
    )
    .subscribe();
}

/**
 * Refreshes the list of cards by dispatching the GetCards action and then redirects
 * the user to a specified page based on the provided widget attributes.
 *
 * @param injector - The Angular Injector used to retrieve necessary services.
 * @param attri - Configuration attributes for the UI zone widget, including
 *                properties for navigation.
 */
export function refreshCardsAndRedirect(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const store = injector.get(Store);
  store.dispatch(new GetCards());
  handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true, false);
}
